<template>
  <div class="wide main-content">
    <section class="section-header">
      <div class="wrap">
        <heading :level="1">{{ pageTitle }}</heading>
        <div
          v-if="subTitle.length"
          v-dompurify-html="subTitle"
          class="sub-title"
        />
        <div
          v-if="hasBlock(helpBlock)"
          class="blocks"
        >
          <div
            v-if="hasItems(helpBlock.items)"
            class="items"
          >
            <default-block
              v-for="(item, key) in helpBlock.items"
              :key="`help_item_${key}`"
              :item="item"
            />
          </div>
        </div>
        <div
          v-if="hasBlock(faqBlock)"
          class="blocks"
        >
          <heading :level="2">{{ faqBlock.title }}</heading>
          <div
            v-if="hasItems(faqBlock.items)"
            class="items"
          >
            <default-block
              v-for="(item, key) in faqBlock.items"
              :key="`faq_item_${key}`"
              :item="item"
            />
          </div>
        </div>

        <div
          v-if="hasBlock(otherTopics)"
          class="blocks other-topics"
        >
          <heading :level="2">{{ otherTopics.title }}</heading>
          <template v-if="hasItems(otherTopics.items)">
            <template v-if="isDesktop">
              <div class="items small">
                <small-block
                  v-for="(item, key) in otherTopics.items"
                  :key="`other_item_${key}`"
                  :item="item"
                />
              </div>
            </template>
            <template v-else>
              <carousel
                ref="slider"
                class="item-wrapper"
                :navigation-enabled="true"
                :pagination-enabled="true"
                :navigation-click-target-size="0"
                :navigation-next-label="''"
                :navigation-prev-label="''"
                :loop="false"
                :per-page="1"
              >
                <slide
                  v-for="(items, index) in getChunkedItems"
                  :key="index"
                >
                  <small-block
                    v-for="(item, key) in items"
                    :key="`other_item_${key}`"
                    :item="item"
                  />
                </slide>
              </carousel>
            </template>
          </template>
        </div>
      </div>
    </section>

    <section
      v-if="hasBlock(representatives)"
      class="representatives"
    >
      <div class="wrap">
        <heading :level="2"> {{ representatives.title }}</heading>

        <div
          class="header"
          :class="{ empty: filteredRepresentatives.length === 0 }"
        >
          <div
            class="imageBlock"
            :style="style"
          >
            <div class="field">
              <label for="country">{{ $t('reusable.country.label') }}</label>
              <element-autocomplete
                :mobile-title="$t('reusable.country.selectTitle')"
                :selected-item="selectedObject"
                name="country"
                field="country"
                :placeholder="$t('helpAndContacts.selectCountry')"
                :items="countryList"
                @change="onCountryChange"
              />
            </div>
          </div>
          <div
            v-if="selectedObject.value !== 'LV'"
            class="textBlock"
          >
            <div class="text">
              <div class="title">{{ representatives.info.title }}</div>
              <div class="sub-text">{{ representatives.info.subTitle }}</div>
            </div>
            <div class="contactBlock">
              <div class="phone">{{ representatives.info.phone }}</div>
              <div
                v-dompurify-html="representatives.info.workingHours"
                class="working_hours"
              />
            </div>
          </div>
        </div>
        <div
          v-if="filteredRepresentatives.length"
          class="representative-list"
        >
          <representative
            v-for="(representative, index) in filteredRepresentatives"
            :key="`representative_${representative.country}_${index}_${representative.name}`"
            :representative="representative"
          />
        </div>
      </div>
    </section>

    <section class="other-content">
      <div class="wrap">
        <div class="container">
          <div
            v-if="hasBlock(forms)"
            class="block"
          >
            <heading :level="2"> {{ forms.title }}</heading>
            <template v-if="hasItems(forms.items)">
              <form-block :items="forms.items" />
            </template>
          </div>
          <div class="block">
            <heading :level="2">{{ $t('searchPage.searchTitle') }}</heading>

            <div class="search-container">
              <input-field
                name="search"
                field="search"
                :label="$t('searchPage.searchPlaceholder')"
                :max-length="255"
                :value="''"
                @input="updateSearchString"
              />
              <blue-button
                class="button"
                :label="$t('searchPage.startSearch')"
                @clicked="doSearch()"
              />
            </div>
          </div>
        </div>

        <div
          v-if="hasBlock(otherInquiries)"
          class="other-inquiries"
        >
          <heading :level="2"> {{ otherInquiries.title }}</heading>
          <div
            v-if="hasItems(otherInquiries.items)"
            class="inquiries"
          >
            <other-block
              v-for="(inquiry, key) in otherInquiries.items"
              :key="inquiry.title"
              :inquiry="inquiry"
              :class="[{ open: inquiry.open || false }]"
              @click="toggleState(key)"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import chunk from 'lodash-es/chunk'
  import cloneDeep from 'lodash-es/cloneDeep'
  import querystring from 'querystring'
  import { Carousel, Slide } from 'vue-carousel'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import uniqBy from 'lodash-es/uniqBy'

  const Heading = () => import(/* webpackChunkName: "heading" */ 'components/Heading')
  const DefaultBlock = () =>
    import(
      /* webpackChunkName: "default-block" */ 'containers/Pages/HelpAndContact/Block'
    )
  const SmallBlock = () =>
    import(
      /* webpackChunkName: "small-block" */ 'containers/Pages/HelpAndContact/SmallBlock'
    )
  const OtherBlock = () =>
    import(
      /* webpackChunkName: "other-block" */ 'containers/Pages/HelpAndContact/OtherBlock'
    )
  const FormBlock = () =>
    import(
      /* webpackChunkName: "form-block" */ 'containers/Pages/HelpAndContact/FormBlock'
    )
  const Representative = () =>
    import(
      /* webpackChunkName: "representative" */ 'containers/Pages/HelpAndContact/RepresentativeItem'
    )
  const InputField = () =>
    import(/* webpackChunkName: "input-field" */ 'components/Elements/Input')
  const BlueButton = () =>
    import(/* webpackChunkName: "btn-blue" */ 'components/Buttons/Blue')
  const ElementAutocomplete = () =>
    import(
      /* webpackChunkName: "element-autocomplete" */ 'components/Elements/Autocomplete'
    )

  export default {
    name: 'HelpAndContact',
    components: {
      Heading,
      DefaultBlock,
      SmallBlock,
      OtherBlock,
      FormBlock,
      Representative,
      InputField,
      BlueButton,
      Carousel,
      Slide,
      ElementAutocomplete
    },
    mixins: [ImageHelpers],
    data() {
      return {
        searchString: '',
        selectedObject: {
          key: '',
          value: '',
          title: this.$t('helpAndContacts.selectCountry')
        },
        countryList: [
          {
            key: 'ALL',
            value: 'ALL',
            title: this.$t('helpAndContacts.showAll')
          }
        ]
      }
    },
    computed: {
      ...mapState({
        pageTitle: (state) => state.helpAndContact.pageTitle,
        subTitle: (state) => state.helpAndContact.subTitle,
        helpBlock: (state) => state.helpAndContact.helpBlock,
        faqBlock: (state) => state.helpAndContact.faqBlock,
        otherTopics: (state) => state.helpAndContact.otherTopics,
        otherInquiries: (state) => state.helpAndContact.otherInquiries,
        forms: (state) => state.helpAndContact.forms,
        representatives: (state) => state.helpAndContact.representatives,
        countries: (state) => state.helpAndContact.countries,
        searchPageUrl: (state) => state.search.searchPageUrl
      }),
      isDesktop() {
        return this.$store.getters['device/isDesktop']
      },
      getChunkedItems() {
        return chunk(this.otherTopics.items, 4)
      },
      filteredRepresentatives() {
        if (this.selectedObject.key === '') {
          return []
        }

        const items = cloneDeep(this.representatives.items)

        if (this.selectedObject.key === 'ALL') {
          return this.sortItem(items)
        }

        return this.sortItem(
          items.filter(({ country }) => country === this.selectedObject.key)
        )
      },
      style() {
        let source = this.$_ImageHelpers_responsiveImage({
          desktop: this.representatives.images.desktop.source,
          tablet: this.representatives.images.tablet.source,
          mobile: this.representatives.images.mobile.source
        })

        if (!source) {
          return ''
        }

        return this.$_ImageHelpers_getBackgroundImage(source)
      }
    },
    mounted() {
      this.generateList()
    },
    methods: {
      sortItem(items) {
        return items.sort((a, b) => {
          const compareByCountryName = this.getCountryName(a.country).localeCompare(
            this.getCountryName(b.country)
          )
          const compareByName = a.name.localeCompare(b.name)

          return compareByCountryName || compareByName
        })
      },
      hasBlock(block) {
        return Object.values(block).length
      },
      hasItems(items) {
        return items && Object.values(items).length
      },
      generateList() {
        if (!Object.keys(this.representatives).length) {
          return
        }

        const list = uniqBy(this.representatives.items, 'country').map(({ country }) => {
          return {
            key: country,
            value: country,
            title: this.getCountryName(country)
          }
        })

        list.sort((a, b) => a.title.localeCompare(b.title))

        this.countryList = [...this.countryList, ...list]
      },
      getCountryName(key) {
        const country = this.countries.find(({ value }) => value === key)

        if (!country) {
          return ''
        }

        return country.title
      },
      toggleState(index) {
        const values = cloneDeep(this.otherInquiries.items)
        values[index].open = !values[index].open

        this.$store.commit('helpAndContact/setOtherInquiriesItems', values, {
          root: true
        })
      },
      updateSearchString(value) {
        this.searchString = value
      },
      doSearch() {
        const query = querystring.stringify({ search: this.searchString })

        if (query !== 'search=') {
          window.open(this.searchPageUrl + `#${query}`, '_blank')
        }
      },
      onCountryChange(value) {
        this.selectedObject = value
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';
  @import '~assets/styles/components/carousel-navigation-common.scss';

  .section-header {
    background-color: #eef1f5;
    padding: 24px 16px 28px;

    @include bp-mf-tablet {
      padding: 0 16px 40px;
    }

    @include bp-mf-desktop {
      padding: 0 0 4px;
    }

    h1 {
      @include defaultFontValues($blue-dark, 32, 42);
      margin: 0 0 10px;

      @include bp-mf-tablet {
        font-size: em(44);
        line-height: 54px;
        margin: 36px 0 24px;
      }
    }

    h2 {
      @include defaultFontValues($blue-dark, 24, 32, 500);
      margin-bottom: 18px;
      width: 70%;

      @include bp-mf-tablet {
        font-size: em(32);
        line-height: 24px;
        margin-bottom: 24px;
        width: 100%;
      }

      @include bp-mf-desktop {
        margin-bottom: 36px;
      }
    }
  }

  .sub-title {
    @include defaultFontValues($blue-dark, 20, 28, 300);
    width: 100%;
    margin-bottom: 28px;

    @include bp-mf-desktop {
      font-size: 22px;
      line-height: 34px;
      width: 66%;
      margin-bottom: 40px;
    }

    /deep/ p {
      margin: 0;
    }

    /deep/ a {
      color: $blue-medium;

      &:link,
      &:visited {
        color: $blue-medium;
      }
    }
  }

  .items {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    margin-bottom: 48px;

    @include bp-mf-tablet {
      flex-direction: row;
      margin-bottom: 60px;
    }
  }

  .item-wrapper /deep/ {
    .VueCarousel-dot-container {
      margin-top: 4px !important;
    }

    .VueCarousel-dot {
      margin: 0 9px !important;
    }

    .VueCarousel-navigation-next {
      top: -30px;
      right: 24px !important;
    }

    .VueCarousel-navigation-prev {
      top: -30px;
      left: auto !important;
      right: 42px !important;
    }
  }

  .VueCarousel-slide {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    margin-right: 16px;

    .small {
      width: calc(50% - 8px);
      margin-bottom: 16px;
      min-height: 160px;

      &:first-child {
        margin-bottom: 0;
      }
    }
  }

  .other-content {
    padding: 28px 16px;
    background: $blue-bg-css;

    h2 {
      margin-bottom: 18px;

      @include bp-mf-tablet {
        margin-bottom: 24px;
      }

      @include bp-mf-desktop {
        margin: 20px 0 30px;
      }
    }

    .container {
      flex-direction: column;

      @include bp-mf-desktop {
        flex-direction: row;
      }
    }

    .other-inquiries {
      margin-top: 20px;
      padding-top: 20px;

      @include bp-mf-tablet {
        margin-top: 0;
        border-top: 1px solid $blue-dividers;
      }

      @include bp-mf-desktop {
        margin-top: 20px;
      }
    }
  }

  .inquiries {
    display: flex;
    flex-direction: column;
  }

  .container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 28px;

    @include bp-mf-tablet {
      flex-direction: row;

      border-top: 1px solid $blue-dividers;
      margin-bottom: 40px;
    }

    @include bp-mf-desktop {
      margin-top: 0;
    }

    h2 {
      margin-bottom: 18px;

      @include bp-mf-desktop {
        margin-bottom: 36px;
      }
    }

    .block {
      width: 100%;
      margin-bottom: 28px;
      display: flex;
      flex-direction: column;

      &:last-child {
        margin-bottom: 0;

        @include bp-mf-tablet {
          padding-bottom: 28px;
        }

        @include bp-mf-desktop {
          padding-bottom: 0;
          border-bottom: 0;
        }
      }

      &:first-child {
        @include bp-mf-tablet {
          margin-bottom: 0;
          border-bottom: 1px solid $blue-dividers;
          padding-bottom: 28px;
        }

        @include bp-mf-desktop {
          border: 0;
          padding-bottom: 0;
        }
      }

      @include bp-mf-desktop {
        width: calc(50% - 10px);
      }

      h2 {
        margin-top: 0;
        margin-bottom: 18px;

        @include bp-mf-tablet {
          margin-top: 30px;
          margin-bottom: 24px;
        }

        @include bp-mf-desktop {
          margin-bottom: 36px;
        }
      }
    }
  }

  .search-container {
    background: #dee2e7;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px 16px;
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: auto;

    @include bp-mf-tablet {
      flex-direction: row;
      justify-content: space-between;
    }

    @include bp-mf-desktop {
      flex-direction: column;
      justify-content: center;
      padding: 24px 48px;
    }
  }

  .input-block {
    width: 100%;
    height: 48px;
    margin-bottom: 16px;

    @include bp-mf-tablet {
      width: calc(80% - 24px);
      margin-bottom: 0;
    }

    @include bp-mf-desktop {
      width: 100%;
      margin-bottom: 16px;
    }

    /deep/ .input-field {
      height: 48px;
      position: relative;
      padding-right: 45px;

      input:not(:placeholder-shown) {
        padding-top: 16px;
      }

      &::after {
        @include icon-font-thin();
        content: $icon-search-medium;
        width: 20px;
        height: 20px;
        position: absolute;
        right: 12px;
        top: calc(50% - 10px);
        font-size: 20px;
      }
    }
  }

  .button {
    @include bp-mf-tablet {
      width: 20%;
      height: 48px !important;
    }

    @include bp-mf-desktop {
      width: 100%;
      height: 52px !important;
    }

    button {
      @include bp-mf-tablet {
        height: 48px;
      }

      @include bp-mf-desktop {
        height: 52px;
      }
    }
  }

  .representatives {
    @include bp-mf-tablet {
      padding: 0 20px;
      background: $blue-bg-css;
    }

    @include bp-mf-desktop {
      background: $blue-bg-css;
    }

    h2 {
      margin: 28px 16px 16px;

      @include bp-mf-tablet {
        margin: 24px 0;
      }

      @include bp-mf-desktop {
        margin: 28px 0 36px;
      }
    }

    .header {
      &.empty {
        margin-bottom: 20px;
      }

      .imageBlock {
        height: 224px;
        display: flex;
        align-items: flex-end;
        padding: 0 16px 33px;
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;

        @include bp-mf-tablet {
          height: 202px;
          align-items: center;
          padding: 0 28px;
          border-radius: 6px 6px 0 0;
        }

        .field {
          width: 100%;

          @include bp-mf-tablet {
            width: 320px;
          }

          label {
            margin-bottom: 12px;
            color: white;
            font-size: em(14);
            line-height: 16px;
            display: inline-block;

            @include bp-mf-desktop {
              font-size: em(16);
              line-height: 19px;
            }
          }

          .autocomplete {
            width: 100%;

            /deep/ .multiselect {
              min-height: 48px !important;
              margin-bottom: 0;

              @include bp-mf-tablet {
                min-height: 56px !important;
              }

              .arrow {
                right: 4px !important;
              }

              .multiselect__tags {
                min-height: 48px !important;
                padding: 6px 60px 6px 16px !important;
                border: none !important;

                @include bp-mf-tablet {
                  min-height: 56px !important;
                  padding: 10px 60px 6px 16px !important;
                }
              }
            }
          }
        }
      }

      .textBlock {
        padding: 32px 16px 20px;
        display: flex;
        flex-direction: column;
        background: rgba($green-base, 0.08);

        @include bp-mf-tablet {
          flex-direction: row;
          justify-content: space-between;
          padding: 54px 44px 32px;
        }

        @include bp-mf-desktop {
          padding: 30px 28px;
          align-items: center;
        }

        .text {
          @include bp-mf-desktop {
            width: 33%;
          }
        }

        .contactBlock {
          width: 100%;
          display: flex;
          flex-direction: column;

          @include bp-mf-tablet {
            width: 50%;
          }

          @include bp-mf-desktop {
            flex-direction: row;
            width: 66%;
            align-items: center;
          }
        }

        .title {
          color: $blue-dark;
          font-size: em(18);
          font-weight: 500;
          line-height: 24px;

          @include bp-mf-tablet {
            font-size: em(20);
            line-height: 24px;
            margin-bottom: 6px;
          }

          @include bp-mf-desktop {
            margin-bottom: 8px;
          }
        }

        .sub-text {
          color: $blue-menu-hover;
          font-size: em(14);
          font-weight: 500;
          line-height: 24px;
        }

        .phone {
          color: $blue-dark;
          font-size: em(32);
          line-height: 26px;
          margin: 18px 0;

          @include bp-mf-tablet {
            margin: 0 0 22px;
          }

          @include bp-mf-desktop {
            font-size: em(40);
            line-height: 26px;
            margin: 0;
            width: 53%;
          }
        }

        .working_hours {
          color: $blue-dark;
          font-size: em(14);
          line-height: 24px;

          @include bp-mf-tablet {
            font-size: em(12);
            line-height: 18px;
          }

          @include bp-mf-desktop {
            margin: 0;
            width: 47%;
            font-size: em(14);
            line-height: 26px;
          }

          /deep/ p {
            margin: 0 0 8px;

            @include bp-mf-tablet {
              margin: 0;
            }
          }

          /deep/ ul {
            margin: 0 0 8px;
            padding: 0;

            @include bp-mf-tablet {
              margin: 0;
            }

            @include bp-mf-desktop {
              margin: 0 0 5px;
            }

            li {
              list-style: none;
            }
          }
        }
      }
    }
  }

  .representative-list {
    margin-bottom: 20px;
  }
</style>
